<template>
  <div class="w-100 existing-catalog">
    <label class="w-100 font-weight-bold">
      {{ $t("GenerateCatalogFromTextFile.ExistingCatalog") }}
    </label>

    <div class="d-flex">
      <b-dropdown class="dropdown-default grow mr-2">
        <template #button-content>
          <text-with-fallback-display :source="selectedSourceCatalog" :language="primaryLanguage" field="name" />
        </template>

        <b-dropdown-item
          v-for="catalog in sortedCatalogs"
          :key="catalog[primaryLanguageCode].catalogId"
          :active="catalog[primaryLanguageCode].catalogId === vm.selectedCatalogId"
          @click="selectCatalogId(catalog)">
          <text-with-fallback-display :source="catalog" :language="primaryLanguage" field="name" />
        </b-dropdown-item>
      </b-dropdown>

      <b-btn class="btn-icon mono" v-b-modal.select-source-catalog>
        <i class="mdp-Open"></i>
      </b-btn>
    </div>

    <manage-catalogs modal-name="select-source-catalog" :included-catalog-types="[0, 1]" dialog-mode="open" @selected-catalog="handleSelectedCatalog" />
  </div>
</template>

<script setup lang="ts">
import { useChannelCreatorState } from "@ChannelCreator/composables/useChannelCreator";
import type { catalog as Catalog } from "@medapro-types/ChannelCreator/store/channel-creator-store";
import TextWithFallbackDisplay from "@common/components/TextWithFallbackDisplay.vue";
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { naturalSort } from "@util/naturalSort";
import { computed, onMounted, reactive, watch } from "vue";
import ManageCatalogs from "../../ManageCatalogs.vue";

//#region Props & Emits
const props = defineProps<{
  selectedExistingCatalogId: string | null;
}>();

const emits = defineEmits(["update:selectedExistingCatalogId", "setCatalogName"]);
//#endregion

//#region Store
const { primaryLanguage } = useContentLanguages();
const { catalogs } = useChannelCreatorState();

//#endregion

//#region ViewModel
type ViewModel = {
  selectedCatalogId: string | null;
  selectedCatalogName: string | null;
};

const vm = reactive<ViewModel>({
  selectedCatalogId: "",
  selectedCatalogName: ""
});

//#endregion

//#region Computed
const { primaryLanguageCode } = useContentLanguages();

const sortedCatalogs = computed(() => naturalSort(catalogs.value, (elem) => elem[primaryLanguageCode.value]?.name));

const selectedSourceCatalog = computed(() => catalogs.value.find((c) => c[primaryLanguageCode.value]?.catalogId === vm.selectedCatalogId));
//#endregion

//#region Methods
const getCatalogName = (catalog: Record<string, Catalog>) => {
  const lang = primaryLanguage.value;
  if (!lang) return "";

  const name = catalog[lang.languageCode]?.name;
  if (name) return name;

  if (lang.fallbackLanguageCode) {
    return catalog[lang.fallbackLanguageCode]?.name || "";
  }

  return "";
};

const selectCatalogId = (catalog: Record<string, Catalog>) => {
  vm.selectedCatalogId = catalog[primaryLanguageCode.value].catalogId;
  vm.selectedCatalogName = getCatalogName(catalog);
};

const handleSelectedCatalog = (selectedSourceCatalogId: string) => {
  vm.selectedCatalogId = selectedSourceCatalogId;
};
//#endregion

//#region Watchers
watch(
  () => vm.selectedCatalogId,
  (nv) => {
    emits("update:selectedExistingCatalogId", nv);
  }
);

watch(
  () => vm.selectedCatalogName,
  (nv) => {
    emits("setCatalogName", nv);
  }
);

//#endregion

//#region Lifecycle
onMounted(() => {
  vm.selectedCatalogId = props.selectedExistingCatalogId;
});
//#endregion
</script>
